import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { fetchActiveCosmetics, equipItem, unequipItem, isOverlay } from '../lib/credits';
import { useAuth } from './AuthContext';

interface CosmeticsCtx {
  activeFrame: string | null;
  activeTitle: string | null;
  activeExperience: string | null;
  activeOverlays: string[];
  activeLightTheme: string | null;
  activeDarkTheme: string | null;
  equip: (slug: string, category: 'frame' | 'title' | 'experience' | 'theme') => Promise<void>;
  unequip: (category: 'frame' | 'title' | 'experience' | 'theme', slug?: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<CosmeticsCtx>({
  activeFrame: null,
  activeTitle: null,
  activeExperience: null,
  activeOverlays: [],
  activeLightTheme: null,
  activeDarkTheme: null,
  equip: async () => {},
  unequip: async () => {},
  refresh: async () => {},
});

export function CosmeticsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeFrame, setActiveFrame] = useState<string | null>(null);
  const [activeTitle, setActiveTitle] = useState<string | null>(null);
  const [activeExperience, setActiveExperience] = useState<string | null>(null);
  const [activeOverlays, setActiveOverlays] = useState<string[]>([]);
  const [activeLightTheme, setActiveLightTheme] = useState<string | null>(null);
  const [activeDarkTheme, setActiveDarkTheme] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    const c = await fetchActiveCosmetics(user.id);
    setActiveFrame(c.activeFrame);
    setActiveTitle(c.activeTitle);
    setActiveExperience(c.activeExperience);
    setActiveOverlays(c.activeOverlays);
    setActiveLightTheme(c.activeLightTheme);
    setActiveDarkTheme(c.activeDarkTheme);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const root = document.documentElement;
    if (activeLightTheme) root.setAttribute('data-light-theme', activeLightTheme);
    else root.removeAttribute('data-light-theme');
    if (activeDarkTheme) root.setAttribute('data-dark-theme', activeDarkTheme);
    else root.removeAttribute('data-dark-theme');
  }, [activeLightTheme, activeDarkTheme]);

  const equip = useCallback(async (slug: string, category: 'frame' | 'title' | 'experience' | 'theme') => {
    if (!user) return;
    await equipItem(user.id, slug, category);
    if (category === 'frame') setActiveFrame(slug);
    else if (category === 'title') setActiveTitle(slug);
    else if (category === 'experience') {
      // Re-read from DB so overlays array vs base theme update correctly.
      await refresh();
    }
    else if (category === 'theme') {
      setActiveLightTheme(slug);
      setActiveDarkTheme(slug);
    }
  }, [user, refresh]);

  const unequip = useCallback(async (category: 'frame' | 'title' | 'experience' | 'theme', slug?: string) => {
    if (!user) return;
    await unequipItem(user.id, category, slug);
    if (category === 'frame') setActiveFrame(null);
    else if (category === 'title') setActiveTitle(null);
    else if (category === 'experience') {
      if (slug && isOverlay(slug)) {
        setActiveOverlays(prev => prev.filter(s => s !== slug));
      } else {
        setActiveExperience(null);
      }
    }
    else if (category === 'theme') {
      setActiveLightTheme(null);
      setActiveDarkTheme(null);
    }
  }, [user]);

  return (
    <Ctx.Provider value={{ activeFrame, activeTitle, activeExperience, activeOverlays, activeLightTheme, activeDarkTheme, equip, unequip, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export const useCosmetics = () => useContext(Ctx);
